import { makeAutoObservable, runInAction } from "mobx";
import { ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { ANALYTICS_EVENTS } from "../../../analytics-store.tsx";
import { BackendError } from "../../../config/axios.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import { DEFAULT_BACKGROUND_CONFIGS, DEFAULT_SIZE_CONFIGS } from "../../../constants/ImageToImageConstants.ts";
import { IRootStore } from "../../../mobx/root-store.tsx";
import {
	ImageToImageConfigType,
	PlaygroundImage,
	PlaygroundResult,
	SelectedConfigsProps,
} from "../../../models/image-to-image.ts";
import { isFoxApps } from "../../../utils/index.ts";
import EditingCanvasStore from "./Config/Features/EraseObject/editing-canvas-store.tsx";
import {
	IManualPlacementStore,
	ManualPlacementStore,
} from "./Config/Features/ManualPlacement/manual-placement-store.tsx";
import { IStyleStore, StyleStore } from "./Config/FoxConfigs/Style/style-store.tsx";
import { IImageToImageGenerationStore, ImageToImageGenerationStore } from "./generation-store.tsx";

export interface IImageToImageStore {
	config: ImageToImageConfigType;
	selected_configs: SelectedConfigsProps;
	activeConfig: string;

	errors: { generate?: BackendError };
	thumbnailNavigationSlideToImage: PlaygroundImage | null;

	isBackgroundConfigEnabled: boolean;

	editingCanvasStore: EditingCanvasStore;
	generationStore: IImageToImageGenerationStore;
	manualPlacementStore: IManualPlacementStore;
	styleStore: IStyleStore;

	handleConfigChange: <K extends keyof ImageToImageConfigType>(key: K, value: ImageToImageConfigType[K]) => void;
	resetConfigChange: () => void;
	handleSelectedConfigsChange: <K extends keyof SelectedConfigsProps>(key: K, value: SelectedConfigsProps[K]) => void;
	handleActiveConfigChange: (config: string) => void;
	handleNumResultsChange: (numResults: number) => void;

	getGeneratedImagesCount: number;
	isGeneratingImages: boolean;

	generateImageToImage: (
		images?: PlaygroundImage[],
		config?: ImageToImageConfigType,
		isFromUpload?: boolean,
		num_results?: number,
		skipTracking?: boolean,
	) => Promise<void>;
	abortImageGeneration: () => void;

	expandBackground: (
		image: PlaygroundImage,
		config?: ImageToImageConfigType,
		keep_aspect_ratio_value_in_config?: boolean,
	) => Promise<void>;
	cropImage: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	increaseImageResolution: (
		image: PlaygroundImage,
		resolution: number,
		config?: ImageToImageConfigType,
	) => Promise<void>;
	mixFaceBody: () => Promise<void>;
	refineColor: () => Promise<void>;
	handleUploadImages: (e: ChangeEvent<HTMLInputElement>, type?: any) => Promise<void>;
	addAIEditorImageInARow: (imageUrl: string) => Promise<void>;
	toggleBackgroundConfig: (status: boolean) => void;
	showAPIError: (error: any) => void;
}

export default class ImageToImageStore implements IImageToImageStore {
	rootStore: IRootStore;
	private _config: ImageToImageConfigType;

	generationStore: IImageToImageGenerationStore;
	manualPlacementStore: IManualPlacementStore;
	styleStore: IStyleStore;
	editingCanvasStore: EditingCanvasStore;

	selected_configs: SelectedConfigsProps = {
		background: [],
		size: [],
		foreground_placement: [],
	};
	activeConfig: string = "";

	errors: { generate?: BackendError } = {};
	thumbnailNavigationSlideToImage: PlaygroundImage | null = null;

	// ===== FEATURE TOGGLES =====
	isBackgroundConfigEnabled = false;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.editingCanvasStore = new EditingCanvasStore(this.rootStore);

		// Initialize sub-stores
		this.generationStore = new ImageToImageGenerationStore(this.rootStore, this);
		this.manualPlacementStore = new ManualPlacementStore(this.rootStore, this);
		this.styleStore = new StyleStore(this.rootStore, this);

		this._config = {
			original_image: { id: "", url: "", playground_result_id: "" },
			num_results: isFoxApps() ? 4 : 1,
			background: DEFAULT_BACKGROUND_CONFIGS,
			size: DEFAULT_SIZE_CONFIGS,
			object_manipulation: this.editingCanvasStore.objectManipulationConfig,
			content_moderation: false,
		};
	}

	// TODO: Relevant to image-to-image-store.tsx
	get config(): ImageToImageConfigType {
		return { ...this._config, object_manipulation: this.editingCanvasStore.objectManipulationConfig };
	}

	// TODO: Relevant to image-to-image-store.tsx
	set config(newConfig: ImageToImageConfigType) {
		if (newConfig.object_manipulation) {
			console.warn("(image-to-image-store) object_manipulation should be set through the editingCanvasStore!");
			this.editingCanvasStore.objectManipulationConfig = {
				...this.editingCanvasStore.objectManipulationConfig,
				...newConfig.object_manipulation,
			};
		}

		this._config = {
			...newConfig,
			object_manipulation: this.editingCanvasStore.objectManipulationConfig,
		};
	}

	get getGeneratedImagesCount(): number {
		const { playgroundStore } = this.rootStore;
		return (
			playgroundStore.getSelectedImages().length *
			(this.selected_configs.background?.length || 1) *
			(this.selected_configs.size?.length || 1) *
			(this.selected_configs.foreground_placement?.length || 1) *
			(this.selected_configs.image_reference?.length || 1)
		);
	}

	// TODO: Relevant to multpile sub-stores, remain in image-to-image-store.tsx
	handleSelectedConfigsChange = <K extends keyof SelectedConfigsProps>(key: K, value: SelectedConfigsProps[K]) => {
		if (value !== undefined) {
			this.selected_configs[key] = value;
		}
	};

	// TODO: Relevant to multpile sub-stores, remain in image-to-image-store.tsx
	handleConfigChange = <K extends keyof ImageToImageConfigType>(key: K, value: ImageToImageConfigType[K]) => {
		runInAction(() => {
			this.config = { ...this.config, [key]: value };
		});
	};

	// TODO: Relevant to multpile sub-stores, remain in image-to-image-store.tsx
	resetConfigChange = () => {
		this.editingCanvasStore.resetObjectManipulationConfig();
		this.config = {
			original_image: { id: "", url: "", playground_result_id: "" },
			num_results: isFoxApps() ? 4 : 1,
			background: DEFAULT_BACKGROUND_CONFIGS,
			size: DEFAULT_SIZE_CONFIGS,
			content_moderation: false,
		};
	};

	// TODO: Relevant to multpile sub-stores, remain in image-to-image-store.tsx
	handleActiveConfigChange = (config: string) => {
		runInAction(() => {
			this.activeConfig = config;
		});
	};

	// TODO: Relevant to multpile sub-stores, remain in image-to-image-store.tsx
	handleNumResultsChange = (numResults: number) => {
		this.handleConfigChange("background", {
			...this.config.background,
			generateByText: {
				...this.config.background?.generateByText,
				num_results: numResults,
			},
			generateByImage: {
				...this.config.background?.generateByImage,
				num_results: numResults,
			},
		});
		this.editingCanvasStore.handleConfigChange("num_results", numResults);
	};

	// ===== GENERATION ACTIONS (Delegated to generationStore) =====
	generateImageToImage = async (
		images?: PlaygroundImage[],
		config?: ImageToImageConfigType,
		isFromUpload?: boolean,
		numResults?: number,
		skipTracking?: boolean,
	): Promise<void> => {
		return this.generationStore.generateImageToImage(images, config, isFromUpload, numResults, skipTracking);
	};

	abortImageGeneration = () => {
		return this.generationStore.abortImageGeneration();
	};

	// Computed getter for backward compatibility
	get isGeneratingImages(): boolean {
		return this.generationStore.isGeneratingImages;
	}

	mixFaceBody = async (): Promise<void> => {
		return this.styleStore.mixFaceBody();
	};

	refineColor = async (): Promise<void> => {
		return this.styleStore.refineColor();
	};

	// TODO: Relevant to image-to-image-store.tsx
	uploadOriginalImage = async (images: PlaygroundImage[]): Promise<void> => {
		await this.generateImageToImage(
			images,
			{
				original_image: { id: "", url: "", playground_result_id: "" },
				background: { original: true },
				num_results: 1,
			},
			true,
			undefined,
			true, // Skip tracking for internal operations
		);
	};

	// TODO: Should remain in image-to-image-store.tsx
	handleUploadImages = async (e: ChangeEvent<HTMLInputElement>, type: any = "upload") => {
		const files: File[] | null = Array.from(e.target.files || []);
		e.target.value = "";

		if (files.length) {
			const { playgroundStore } = this.rootStore;
			const resultsSkeletons: PlaygroundResult = {
				id: uuidv4(),
				config: { ...this.config },
				type: type,
				images: Array.from({
					length: files.length,
				}).map((_) => ({
					id: "",
					url: "",
					loading: true,
					playground_result_id: "",
				})),
			};

			playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
			const imagesBeforeUpload: PlaygroundResult = {
				id: uuidv4(),
				config: { ...this.config },
				type: type,
				images: await Promise.all(
					files.map(async (file) => {
						return {
							id: uuidv4(),
							url: URL.createObjectURL(file),
							file: file,
							type: type,
							offline_props: {
								prefix_id: file.name.split(".")[0],
								variation: 0,
							},
							playground_result_id: "",
						};
					}),
				),
			};

			isFoxApps()
				? await this.styleStore.nflRmbgBeforeStyle(imagesBeforeUpload.images)
				: await this.uploadOriginalImage(imagesBeforeUpload.images);

			runInAction(async () => {
				this.rootStore.playgroundStore.selectImages(imagesBeforeUpload.images);
			});
		}
	};

	// TODO: Add to AIEditor sub-store
	addAIEditorImageInARow = async (imageUrl: string) => {
		const response = await fetch(imageUrl);
		const blob = await response.blob();
		const filename = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);
		const { playgroundStore } = this.rootStore;

		const analyticsUploadedImageUrl = new URL(imageUrl);
		const analyticsUploadedImageId = analyticsUploadedImageUrl.searchParams.get("imageId");
		const imagesBeforeUpload: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.config },
			type: "save",
			images: [
				{
					id: uuidv4(),
					url: imageUrl,
					file: new File([blob], filename, { type: blob.type }),
					type: APPS.IMAGE_TO_IMAGE,
					config: { ...this.config },
					sessionId:
						imageUrl.split("?")[0] +
						(analyticsUploadedImageId ? `?imageId=${analyticsUploadedImageId}` : ""),
					playground_result_id: "",
				},
			],
		};

		if (analyticsUploadedImageId) {
			this.rootStore.analyticsStore.logImageToImageEvent(
				ANALYTICS_EVENTS.PLAYGROUND_IMAGE_SAVE_ITI,
				imagesBeforeUpload.images[0],
			);
		} else {
			const existingItiImage = playgroundStore.getAvailableImages().find((image) => imageUrl === image?.url);
			if (existingItiImage) {
				this.rootStore.analyticsStore.logImageToImageEvent(
					ANALYTICS_EVENTS.PLAYGROUND_IMAGE_SAVE_ITI,
					existingItiImage,
				);
			}
		}

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, imagesBeforeUpload];
	};

	// TODO: Add to background-edit sub-store
	toggleBackgroundConfig = (status: boolean) => {
		runInAction(() => {
			this.isBackgroundConfigEnabled = status;
		});
	};

	expandBackground = async (
		image: PlaygroundImage,
		config?: ImageToImageConfigType,
		keep_aspect_ratio_value_in_config?: boolean,
	): Promise<void> => {
		return this.manualPlacementStore.expandBackground(image, config, keep_aspect_ratio_value_in_config);
	};

	cropImage = async (image: PlaygroundImage, config?: ImageToImageConfigType): Promise<void> => {
		return this.manualPlacementStore.cropImage(image, config);
	};

	increaseImageResolution = async (
		image: PlaygroundImage,
		resolution: number,
		config?: ImageToImageConfigType,
	): Promise<void> => {
		return this.manualPlacementStore.increaseImageResolution(image, resolution, config);
	};

	// TODO: Should remain in image-to-image-store.tsx
	showAPIError = (error: any) => {
		runInAction(() => {
			if (error?.message.includes("422") || error?.response?.status === 422) {
				this.errors.generate = { status: +error?.response?.status || 422, message: error?.message ?? "" };
			} else if (error?.message.includes("460") || error?.response?.status === 460) {
				// Obfuscated error message given from API v1
				this.errors.generate = { status: +error?.response?.status || 460, message: error?.message ?? "" };
			} else if (error?.message.includes("415") || error?.response?.status === 415) {
				// Unsupported file's mime type
				this.errors.generate = { status: +error?.response?.status || 415, message: error?.message ?? "" };
			}
		});
	};
}
